import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const TYPE_META = {
  call_in:     { icon: '\uD83D\uDCF2', label: 'Incoming Call', color: '#10b981' },
  call_out:    { icon: '\uD83D\uDCDE', label: 'Outgoing Call', color: '#3b82f6' },
  call_missed: { icon: '\u260E\uFE0F',  label: 'Missed Call',   color: '#ef4444' },
  sms_in:      { icon: '\uD83D\uDCE9', label: 'SMS Received',  color: '#8b5cf6' },
  sms_out:     { icon: '\uD83D\uDCE4', label: 'SMS Sent',      color: '#6366f1' },
};

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
}

function formatDuration(seconds) {
  if (!seconds || seconds === 0) return '-';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

export default function CallLog({ kidId, parentId, deviceFilter }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | calls | sms

  useEffect(() => {
    if (!parentId) { setLoading(false); return; }
    const load = async () => {
      setLoading(true);
      let query = supabase.from('call_sms_log').select('*').eq('parent_id', parentId);
      if (kidId) query = query.eq('kid_id', kidId);
      const { data } = await query.order('timestamp', { ascending: false }).limit(200);
      setLogs(data || []);
      setLoading(false);
    };
    load();
  }, [parentId, kidId, deviceFilter]);

  // Phone-only feature
  if (deviceFilter === 'mac') {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">{'\uD83D\uDCDE'} Calls & SMS</h1>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>{'\uD83D\uDDA5\uFE0F'}</div>
          <h3 style={{ marginBottom: 8 }}>Call & SMS monitoring is phone-only</h3>
          <p style={{ fontSize: 14, color: '#64748b' }}>
            Switch to <strong>Phone</strong> or <strong>All</strong> to see call/SMS data.
          </p>
        </div>
      </div>
    );
  }

  const filtered = logs.filter(l => {
    if (filter === 'calls') return l.type?.startsWith('call');
    if (filter === 'sms') return l.type?.startsWith('sms');
    return true;
  });

  const callCount = logs.filter(l => l.type?.startsWith('call')).length;
  const smsCount = logs.filter(l => l.type?.startsWith('sms')).length;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-icon">{'\uD83D\uDCDE'}</div>
        <div className="loading-title">Loading call & SMS log...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          {'\uD83D\uDCDE'} Calls & SMS{' '}
          <span className="badge badge-blue">{logs.length}</span>
        </h1>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-label">Total Calls</div>
          <div className="stat-value">{callCount}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total SMS</div>
          <div className="stat-value">{smsCount}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="filter-bar" style={{ marginBottom: 16 }}>
        {['all', 'calls', 'sms'].map(f => (
          <button key={f} className="btn"
            style={{
              background: filter === f ? '#3b82f6' : '#e2e8f0',
              color: filter === f ? '#fff' : '#475569',
              fontSize: 12, padding: '6px 14px', marginTop: 0,
            }}
            onClick={() => setFilter(f)}>
            {f === 'all' ? 'All' : f === 'calls' ? 'Calls' : 'SMS'}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>{'\uD83D\uDCF1'}</div>
          <h3 style={{ marginBottom: 8 }}>No call or SMS data yet</h3>
          <p style={{ fontSize: 14, color: '#64748b' }}>
            Data will appear once the Android app has phone permissions granted.
          </p>
        </div>
      ) : (
        <div className="card">
          {filtered.map((log, i) => {
            const meta = TYPE_META[log.type] || { icon: '\u2753', label: log.type, color: '#94a3b8' };
            return (
              <div key={log.id} style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0',
                borderBottom: i < filtered.length - 1 ? '1px solid rgba(0,0,0,0.06)' : 'none',
              }}>
                <div style={{ fontSize: 24, width: 36, textAlign: 'center' }}>{meta.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {log.contact_name || log.phone_number || 'Unknown'}
                  </div>
                  <div style={{ fontSize: 12, color: meta.color, fontWeight: 600 }}>{meta.label}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  {log.duration_seconds && log.duration_seconds > 0 && (
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{formatDuration(log.duration_seconds)}</div>
                  )}
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    {formatTime(log.timestamp).split(', ')[1] || formatTime(log.timestamp)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
